import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from './ui/button';

// Shared furniture for the admin pages: the row of links across the top and
// the password gate. Defined once so a new admin page is one import rather
// than another copy of the same login form.

export const ADMIN_LINKS = [
    ['/admin', 'Dashboard'],
    ['/admin/changelog', 'Changelog'],
    ['/admin/help', 'Help & Guide'],
    ['/admin/blog', 'Blog'],
];

/** Links across the top of every admin page, current page marked. */
export const AdminNav = () => {
    const { pathname } = useLocation();
    return (
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 pb-4 mb-6 border-b border-stone-200">
            {ADMIN_LINKS.map(([to, label]) => {
                const current = pathname === to;
                return (
                    <Link
                        key={to}
                        to={to}
                        className={`text-sm ${current
                            ? 'font-bold text-foreground'
                            : 'text-primary underline hover:no-underline'}`}
                        aria-current={current ? 'page' : undefined}
                    >
                        {label}
                    </Link>
                );
            })}
        </div>
    );
};

/**
 * Password gate. The password is checked against a real admin endpoint rather
 * than trusted locally, then kept in sessionStorage for the other pages, which
 * is why logging in once covers all of them until the tab is closed.
 */
export const AdminLogin = ({ onAuthed, title = 'Mimique Admin' }) => {
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const base = process.env.REACT_APP_BACKEND_URL || '';

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const res = await fetch(`${base}/api/admin/changelog`, {
                headers: { Authorization: `Bearer ${password}` },
            });
            if (res.status === 401) { toast.error('Wrong password'); return; }
            sessionStorage.setItem('mimique_admin_token', password);
            onAuthed(password);
        } catch {
            toast.error('Could not reach server');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-background flex items-center justify-center px-4">
            <form onSubmit={handleLogin} className="w-full max-w-xs space-y-4">
                <h1 className="text-xl font-bold text-foreground">{title}</h1>
                <input
                    type="password"
                    placeholder="Admin password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm bg-stone-50 focus:outline-none focus:ring-2 focus:ring-primary"
                    autoFocus
                />
                <Button type="submit" className="w-full rounded-full" disabled={loading}>
                    {loading ? 'Checking…' : 'Log in'}
                </Button>
            </form>
        </div>
    );
};
